import barba from "@barba/core";

let flavorDropdownListenersBound = false;

const FLAVOR_DROPDOWN_CLOSE_MS = 500;
const FLAVOR_DROPDOWN_CLOSED_HEIGHT = 48;

function getFlavorDropdownCollapse(details) {
  return details.querySelector(".product-flavor-dropdown__collapse");
}

function getFlavorDropdownCollapseInner(details) {
  return details.querySelector(".product-flavor-dropdown__collapse-inner");
}

function getFlavorDropdownScroll(details) {
  return details.querySelector("[data-flavor-scroll]");
}

function resetFlavorDropdownScrollToTop(details) {
  const scroll = getFlavorDropdownScroll(details);
  if (scroll) {
    scroll.scrollTop = 0;
  }
}

function getFlavorDropdownScrollbar(details) {
  return {
    bar: details.querySelector("[data-flavor-scrollbar]"),
    topFill: details.querySelector("[data-flavor-scrollbar-top]"),
    thumb: details.querySelector("[data-flavor-scrollbar-thumb]"),
    bottomFill: details.querySelector("[data-flavor-scrollbar-bottom]"),
  };
}

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function getFlavorDropdownTargetHeight(details) {
  const collapseInner = getFlavorDropdownCollapseInner(details);
  return collapseInner?.offsetHeight || collapseInner?.scrollHeight || 240;
}

function syncFlavorDropdownScrollbar(details) {
  const scroll = getFlavorDropdownScroll(details);
  const { bar, topFill, thumb, bottomFill } = getFlavorDropdownScrollbar(details);
  if (!scroll || !bar || !topFill || !thumb || !bottomFill) {
    return;
  }

  const maxScroll = scroll.scrollHeight - scroll.clientHeight;
  if (maxScroll <= 0) {
    bar.dataset.visible = "false";
    topFill.style.height = "0px";
    thumb.style.height = "0px";
    bottomFill.style.height = "0px";
    return;
  }

  const barHeight = bar.offsetHeight || 160;
  const gap = 4;
  const thumbHeight = Math.min(barHeight, 62);
  const maxThumbTop = Math.max(0, barHeight - thumbHeight);
  const progress = maxScroll > 0 ? scroll.scrollTop / maxScroll : 0;
  const thumbTop = Math.round(maxThumbTop * progress);
  const topHeight = Math.max(0, thumbTop - gap);
  const bottomTop = Math.min(barHeight, thumbTop + thumbHeight + gap);
  const bottomHeight = Math.max(0, barHeight - bottomTop);

  bar.dataset.visible = "true";
  topFill.style.height = `${topHeight}px`;
  topFill.style.display = topHeight > 0 ? "block" : "none";
  thumb.style.top = `${thumbTop}px`;
  thumb.style.height = `${thumbHeight}px`;
  bottomFill.style.top = `${bottomTop}px`;
  bottomFill.style.height = `${bottomHeight}px`;
  bottomFill.style.display = bottomHeight > 0 ? "block" : "none";
}

function bindFlavorDropdownScrollbar(details) {
  const scroll = getFlavorDropdownScroll(details);
  if (!scroll || scroll.dataset.flavorScrollbarBound === "true") {
    return;
  }

  scroll.dataset.flavorScrollbarBound = "true";
  scroll.addEventListener(
    "scroll",
    () => {
      syncFlavorDropdownScrollbar(details);
    },
    { passive: true }
  );

  if (typeof ResizeObserver === "function") {
    const resizeObserver = new ResizeObserver(() => {
      syncFlavorDropdownScrollbar(details);
    });
    resizeObserver.observe(scroll);
  }
}

/**
 * After <details> opens, animate height 0px → target height.
 */
function scheduleFlavorDropdownOpenAnimation(details) {
  const collapse = getFlavorDropdownCollapse(details);
  if (!collapse || !details.open) {
    return;
  }

  bindFlavorDropdownScrollbar(details);
  syncFlavorDropdownScrollbar(details);
  const targetHeight = getFlavorDropdownTargetHeight(details);

  if (prefersReducedMotion()) {
    collapse.style.height = `${targetHeight}px`;
    requestAnimationFrame(() => syncFlavorDropdownScrollbar(details));
    return;
  }

  const run = () => {
    if (!details.open) {
      return;
    }

    collapse.style.transition = "none";
    collapse.style.height = `${FLAVOR_DROPDOWN_CLOSED_HEIGHT}px`;
    void collapse.getBoundingClientRect();
    collapse.style.removeProperty("transition");

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!details.open) {
          collapse.style.removeProperty("height");
          collapse.style.removeProperty("transition");
          return;
        }
        collapse.style.height = `${targetHeight}px`;
        syncFlavorDropdownScrollbar(details);
      });
    });
  };

  run();
}

function animateFlavorDropdownOpen(details) {
  const collapse = getFlavorDropdownCollapse(details);
  if (!details || !collapse || details.open) {
    return;
  }

  document.querySelectorAll("details.product-flavor-dropdown").forEach((d) => {
    if (d !== details) {
      animateFlavorDropdownClose(d, { instant: true });
    }
  });

  collapse.style.transition = "none";
  collapse.style.height = `${FLAVOR_DROPDOWN_CLOSED_HEIGHT}px`;
  details.dataset.flavorOpenHandled = "true";
  details.setAttribute("open", "");

  resetFlavorDropdownScrollToTop(details);
  bindFlavorDropdownScrollbar(details);
  scheduleFlavorDropdownOpenAnimation(details);
}

/**
 * Close flavor <details> after animating the collapse height.
 * @param {HTMLDetailsElement} details
 * @param {{ instant?: boolean, onAfterClose?: () => void }} options
 */
export function animateFlavorDropdownClose(details, options = {}) {
  const { instant = false, onAfterClose } = options;
  const collapse = getFlavorDropdownCollapse(details);
  if (!details || !collapse) {
    details?.removeAttribute("open");
    onAfterClose?.();
    return;
  }

  if (!details.open) {
    collapse.style.height = "";
    delete collapse.dataset.flavorClosing;
    onAfterClose?.();
    return;
  }

  if (instant) {
    collapse.style.height = "";
    delete collapse.dataset.flavorClosing;
    details.removeAttribute("open");
    onAfterClose?.();
    return;
  }

  if (prefersReducedMotion()) {
    collapse.style.height = "";
    delete collapse.dataset.flavorClosing;
    details.removeAttribute("open");
    onAfterClose?.();
    return;
  }

  if (collapse.dataset.flavorClosing === "true") {
    return;
  }
  collapse.dataset.flavorClosing = "true";

  collapse.style.height = `${getFlavorDropdownTargetHeight(details)}px`;
  void collapse.getBoundingClientRect();
  collapse.style.height = `${FLAVOR_DROPDOWN_CLOSED_HEIGHT}px`;

  const finish = () => {
    delete collapse.dataset.flavorClosing;
    collapse.style.height = "";
    details.removeAttribute("open");
    onAfterClose?.();
  };

  const onEnd = (ev) => {
    if (ev.target !== collapse) {
      return;
    }
    if (ev.propertyName !== "height") {
      return;
    }
    collapse.removeEventListener("transitionend", onEnd);
    finish();
  };

  collapse.addEventListener("transitionend", onEnd);

  window.setTimeout(() => {
    if (collapse.dataset.flavorClosing === "true") {
      collapse.removeEventListener("transitionend", onEnd);
      finish();
    }
  }, FLAVOR_DROPDOWN_CLOSE_MS);
}

function navigateFromFlavorDropdownRow(anchor) {
  const href = anchor.href;
  if (document.documentElement.dataset.prefetching === "false") {
    window.location.assign(href);
    return;
  }
  try {
    barba.go(href, anchor);
  } catch {
    window.location.assign(href);
  }
}

function initFlavorDropdown() {
  if (flavorDropdownListenersBound) {
    return;
  }
  flavorDropdownListenersBound = true;

  document.addEventListener(
    "click",
    (e) => {
      if (e.button !== 0) {
        return;
      }
      const rowAnchor = e.target.closest("a.product-flavor-dropdown__row");
      if (rowAnchor) {
        const details = rowAnchor.closest("details.product-flavor-dropdown");
        if (details?.open) {
          if (e.defaultPrevented) {
            return;
          }
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
            return;
          }
          if (rowAnchor.target === "_blank") {
            return;
          }
          const download = rowAnchor.getAttribute("download");
          if (download !== null && download !== "") {
            return;
          }
          const hrefAttr = rowAnchor.getAttribute("href");
          if (!hrefAttr || hrefAttr === "#" || hrefAttr.startsWith("#")) {
            return;
          }

          e.preventDefault();
          e.stopImmediatePropagation();

          animateFlavorDropdownClose(details, {
            onAfterClose: () => navigateFromFlavorDropdownRow(rowAnchor),
          });
          return;
        }
      }

      const summary = e.target.closest(
        "details.product-flavor-dropdown > summary.product-flavor-dropdown__trigger"
      );
      if (summary) {
        const details = summary.closest("details.product-flavor-dropdown");
        if (details?.open) {
          e.preventDefault();
          e.stopImmediatePropagation();
          animateFlavorDropdownClose(details);
        } else if (details) {
          e.preventDefault();
          e.stopImmediatePropagation();
          animateFlavorDropdownOpen(details);
        }
        return;
      }

      const insideFlavor = e.target.closest("details.product-flavor-dropdown");
      if (!insideFlavor) {
        document.querySelectorAll("details.product-flavor-dropdown[open]").forEach((d) => {
          animateFlavorDropdownClose(d);
        });
      }
    },
    true
  );

  document.body.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-flavor-dropdown-close]");
    if (!btn) {
      return;
    }
    const details = btn.closest("details.product-flavor-dropdown");
    if (details) {
      animateFlavorDropdownClose(details);
    }
  });

  document.addEventListener(
    "toggle",
    (e) => {
      const details = e.target;
      if (!details?.classList?.contains("product-flavor-dropdown")) {
        return;
      }

      if (details.open) {
        if (details.dataset.flavorOpenHandled === "true") {
          delete details.dataset.flavorOpenHandled;
          return;
        }

        document.querySelectorAll("details.product-flavor-dropdown").forEach((d) => {
          if (d !== details) {
            animateFlavorDropdownClose(d, { instant: true });
          }
        });
        resetFlavorDropdownScrollToTop(details);
        bindFlavorDropdownScrollbar(details);
        scheduleFlavorDropdownOpenAnimation(details);
      } else {
        const collapse = getFlavorDropdownCollapse(details);
        const { bar } = getFlavorDropdownScrollbar(details);
        if (collapse) {
          collapse.style.height = "";
          delete collapse.dataset.flavorClosing;
        }
        if (bar) {
          bar.dataset.visible = "false";
        }
      }
    },
    true
  );
}

export function updateFlavorSlider() {
  initFlavorDropdown();

  document.querySelectorAll("details.product-flavor-dropdown").forEach((el) => {
    bindFlavorDropdownScrollbar(el);
    animateFlavorDropdownClose(el, { instant: true });
  });

  const swiperEl = document.querySelector("swiper-container.product-flavor-slider");
  if (swiperEl && swiperEl.swiper) {
    swiperEl.swiper.update();
    swiperEl.swiper.navigation.update();

    const slides = swiperEl.querySelectorAll("swiper-slide");
    let targetIndex = -1;
    slides.forEach((slide, idx) => {
      if (slide.querySelector('a[aria-current="page"]')) {
        targetIndex = idx;
      }
    });

    if (targetIndex > 3) {
      swiperEl.swiper.slideTo(targetIndex, 0);
    }

    if (
      swiperEl.swiper.params.freeMode &&
      [3, 7, 11].includes(targetIndex) &&
      window.innerWidth < 768
    ) {
      const currentTranslate = swiperEl.swiper.getTranslate();
      swiperEl.swiper.setTranslate(currentTranslate - 60);
      swiperEl.swiper.updateProgress();
      swiperEl.swiper.updateActiveIndex();
      swiperEl.swiper.updateSlidesClasses();
    }
  }
}
